import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";

const PREFERRED_COUNTRY_ORDER = ["Egypt", "Jordan", "Morocco", "Tunisia"];

const toNumber = (value) => {
  const parsed = Number(value);
  return value !== null && value !== "" && Number.isFinite(parsed) ? parsed : null;
};

const normalizeField = (field) => ({
  ...field,
  id: String(field.id),
  country: (field.country || "Unspecified").trim(),
  city: field.region || "",
  west: toNumber(field.longitude) ?? toNumber(field.bbox_west),
  south: toNumber(field.latitude) ?? toNumber(field.bbox_south),
  variety: field.variety || "N/A",
  acres: field.acres ?? "N/A",
});

export default function useMyFields() {
  const [fields, setFields] = useState([]);
  const [owners, setOwners] = useState([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState("");
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [canSelectOwner, setCanSelectOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setFields([]);

    api.get("/fields/accessible/", {
      params: selectedOwnerId ? { owner: selectedOwnerId } : {},
    })
      .then((response) => {
        if (!active) return;
        const payload = response.data || {};
        const owner = payload.selected_owner || null;
        const ownerId = owner?.id == null ? "" : String(owner.id);
        setOwners(Array.isArray(payload.owners) ? payload.owners : []);
        setSelectedOwner(owner);
        setCanSelectOwner(Boolean(payload.can_select_owner));
        setFields((payload.results || []).map(normalizeField));
        if (ownerId && ownerId !== selectedOwnerId) {
          setSelectedOwnerId(ownerId);
        }
      })
      .catch((requestError) => {
        if (!active) return;
        setFields([]);
        setError(
          requestError?.response?.data?.detail || "Could not load accessible fields."
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedOwnerId]);

  const fieldsByCountry = useMemo(
    () => fields.reduce((groups, field) => {
      if (!groups[field.country]) groups[field.country] = [];
      groups[field.country].push(field);
      return groups;
    }, {}),
    [fields]
  );

  const countries = useMemo(() => {
    const values = Object.keys(fieldsByCountry);
    return values.sort((a, b) => {
      const aIndex = PREFERRED_COUNTRY_ORDER.indexOf(a);
      const bIndex = PREFERRED_COUNTRY_ORDER.indexOf(b);
      if (aIndex !== -1 || bIndex !== -1) {
        return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
      }
      return a.localeCompare(b);
    });
  }, [fieldsByCountry]);

  return {
    fields,
    fieldsByCountry,
    countries,
    owners,
    selectedOwner,
    selectedOwnerId,
    setSelectedOwnerId,
    canSelectOwner,
    isViewingOwnFields: Boolean(selectedOwner?.is_current_user),
    loading,
    error,
  };
}
